<template>
  <v-container fluid class="pa-4">
    <v-snackbar
      v-model="snackbar"
      class="elevation-24"
      vertical
      :color="snackbarColor"
      timeout="4000"
    >
      <div class="text-subtitle-2 font-weight-bold mb-1">
        {{ snackbarColor === 'success' ? 'Check-in Successful' : snackbarColor === 'red' ? 'Not Found' : 'Already Checked In' }}
      </div>
      <p>{{ snackbarText }}</p>
      <template v-slot:actions>
        <v-btn variant="text" color="white" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-row>
      <!-- Camera / Scanner column -->
      <v-col cols="12" md="6">
        <v-card variant="outlined" class="pa-3 mb-4" rounded="xl">
          <div class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center" style="gap:8px;">
            <v-icon color="grey-darken-3">mdi-camera</v-icon>
            Camera Scanner
          </div>
          <qrcode-stream
            :paused="paused"
            @detect="onDetect"
            @camera-on="onCameraOn"
            @camera-off="onCameraOff"
            @error="onError"
            :constraints="{ facingMode: 'environment', width: 500, height: 500 }"
            style="border-radius: 12px; overflow: hidden;"
          >
            <div v-show="showScanConfirmation" class="d-flex align-center justify-center fill-height">
              <v-chip color="grey-darken-3" prepend-icon="mdi-camera-off" size="large">Camera off</v-chip>
            </div>
          </qrcode-stream>
        </v-card>

        <!-- Manual input -->
        <v-card variant="outlined" class="pa-4" rounded="xl">
          <div class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center" style="gap:8px;">
            <v-icon color="grey-darken-3">mdi-keyboard</v-icon>
            Manual / Scanner Input
          </div>
          <p class="text-body-2 text-medium-emphasis mb-3">
            Focus this field and scan a QR code with a handheld scanner, or type the code manually.
          </p>
          <v-text-field
            v-model="result"
            label="QR Code value"
            ref="qr"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-qrcode"
            clearable
            @change="handleChange"
            @focus="valid = true"
            @blur="valid = false"
          />
        </v-card>
      </v-col>

      <!-- Attendee list column -->
      <v-col cols="12" md="6">
        <v-card variant="outlined" rounded="xl">
          <div class="pa-4 pb-2">
            <div class="text-subtitle-1 font-weight-medium mb-3 d-flex align-center" style="gap:8px;">
              <v-icon color="grey-darken-3">mdi-account-group</v-icon>
              Attendee List
            </div>
            <v-text-field
              v-model="search"
              label="Search attendees"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
            />
          </div>

          <v-data-table
            :headers="headers"
            :items="invitations"
            item-key="id"
            :items-per-page="8"
            :search="search"
            density="compact"
          >
            <template v-slot:[`item.checked_in`]="{ item }">
              <v-icon :color="item.checked_in == 1 ? 'success' : 'grey-lighten-1'" size="20">
                {{ item.checked_in == 1 ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
            </template>
            <template v-slot:[`item.checkin_time`]="{ item }">
              <span class="text-caption text-medium-emphasis">{{ formatDateTime(item.checkin_time) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { QrcodeStream } from "vue-qrcode-reader";
import axios from "axios";

export default {
  components: { QrcodeStream },
  data() {
    return {
      result: "",
      search: "",
      paused: false,
      showScanConfirmation: false,
      valid: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      events: [],
      invitations: [],
      headers: [
        { title: "Name",         value: "name",         sortable: true },
        { title: "Email",        value: "email" },
        { title: "Event",        value: "event_name" },
        { title: "Checked In",   value: "checked_in" },
        { title: "Check-in Time",value: "checkin_time" },
      ],
    };
  },
  mounted() {
    this.fetchEvents();
    this.fetchInvitations();
    this.$nextTick(() => {
      this.$refs.qr?.focus();
    });
  },
  methods: {
    async handleChange() {
      if (!this.result?.trim()) return;
      try {
        const response = await axios.post("/api/checkin", { qrData: this.result.trim() });
        this.showSnackbar(response.data.message, "success");
        this.fetchInvitations();
      } catch (error) {
        const msg = error.response?.data?.message || "Check-in failed";
        this.showSnackbar(msg, msg === "Attendee not found" ? "red" : "orange");
      }
      this.paused = true;
      await this.timeout(500);
      this.paused = false;
      this.result = "";
    },
    showSnackbar(text, color) {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },
    formatDateTime(datetime) {
      if (!datetime) return "—";
      const date = new Date(datetime);
      if (isNaN(date.getTime())) return "—";
      const pad = (n) => n.toString().padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    onCameraOn()  { this.showScanConfirmation = false; },
    onCameraOff() { this.showScanConfirmation = true;  },
    onError: console.error,
    async onDetect(detectedCodes) {
      this.result = detectedCodes.map((c) => c.rawValue)[0];
      await this.handleChange();
    },
    timeout(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    },
    async fetchInvitations() {
      try {
        const response = await axios.get("/api/attendees");
        this.invitations = response.data;
      } catch (error) {
        console.error("Error fetching attendees:", error);
      }
    },
    async fetchEvents() {
      try {
        const response = await axios.get("/api/events");
        this.events = response.data;
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    },
    getEventNameById(eventId) {
      const event = this.events.find((e) => e.id == eventId);
      return event ? event.name : "Unknown";
    },
  },
};
</script>
